import { createContext, useContext, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle, X, Mic } from "lucide-react";

const BackgroundTranscriptionCtx = createContext(null);

export const useBackgroundTranscription = () => useContext(BackgroundTranscriptionCtx);

/**
 * Manages transcription jobs that survive React component unmounts / page navigation.
 * Jobs are stored as refs so the polling loop never gets garbage-collected.
 */
export function BackgroundTranscriptionProvider({ children, api }) {
  const [jobs, setJobs] = useState([]); // [{id, patientName, status, progress}]
  const jobCallbacksRef = useRef({}); // { [jobId]: onComplete callback }

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));
  }, []);

  const removeJob = useCallback((id) => {
    setJobs(prev => prev.filter(j => j.id !== id));
    delete jobCallbacksRef.current[id];
  }, []);

  /**
   * Submit a transcription job to run in the background.
   * @param {Blob} blob - audio blob
   * @param {Object} opts - { patientName, liveTranscript, sessionId, saveTranscriptFn, onComplete }
   */
  const submitTranscription = useCallback(async (blob, opts = {}) => {
    const {
      patientName = "Unknown",
      liveTranscript = "",
      sessionId = "",
      saveTranscriptFn,
      onComplete,
    } = opts;

    const localJobId = `bg_${Date.now()}`;

    // Register the callback so it can be called even after navigation
    if (onComplete) {
      jobCallbacksRef.current[localJobId] = onComplete;
    }

    setJobs(prev => [...prev, {
      id: localJobId,
      patientName,
      status: "uploading",
      progress: "Uploading audio...",
    }]);

    try {
      const formData = new FormData();
      formData.append("file", blob, "recording.webm");
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);

      updateJob(localJobId, { progress: `Uploading ${sizeMB}MB...` });

      const startRes = await api.post("/notes/transcribe", formData, {
        timeout: 120000,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");

      updateJob(localJobId, { status: "transcribing", progress: "Transcribing audio..." });

      // Poll for completion — this runs detached from any component
      const pollStart = Date.now();
      while (Date.now() - pollStart < 900000) {
        await new Promise(r => setTimeout(r, 3000));
        let pollRes;
        try {
          pollRes = await api.get(`/notes/transcribe-status/${jobId}`, { timeout: 15000 });
        } catch (pollErr) {
          if (pollErr.code === "ECONNABORTED" || pollErr.code === "ERR_NETWORK") continue;
          throw pollErr;
        }
        const { status: jStatus, data: result, detail: jError, progress } = pollRes.data;
        if (progress?.message) {
          updateJob(localJobId, { progress: progress.message });
        }
        if (jStatus === "complete" && result) {
          const whisperText = result.transcript || "";
          const durationInfo = result.duration_min ? ` (${result.duration_min} min)` : "";
          const langNames = { hi: "Hindi", gu: "Gujarati", ur: "Urdu", es: "Spanish", mr: "Marathi", ta: "Tamil", en: "English" };
          const detectedLang = result.detected_language;
          const langInfo = detectedLang && detectedLang !== "en" ? ` — translated from ${langNames[detectedLang] || detectedLang}` : "";

          const finalText = (liveTranscript && whisperText)
            ? `${whisperText}\n\n--- Speaker-Tagged Live Notes ---\n${liveTranscript}`
            : (whisperText || liveTranscript);

          // Save to backend
          if (saveTranscriptFn && finalText?.trim()) {
            try { await saveTranscriptFn(finalText, "completed"); } catch {}
          }

          // Notify the originating component if it's still mounted
          const cb = jobCallbacksRef.current[localJobId];
          if (cb) cb(finalText);

          updateJob(localJobId, { status: "done", progress: `Done${durationInfo}${langInfo}` });
          toast.success(`Transcription complete for ${patientName}${durationInfo}`, { duration: 5000 });

          // Auto-remove after 8s
          setTimeout(() => removeJob(localJobId), 8000);
          return;
        }
        if (jStatus === "error") throw new Error(jError || "Transcription failed");
      }
      throw new Error("Transcription timed out");
    } catch (err) {
      updateJob(localJobId, { status: "error", progress: err.message || "Failed" });
      if (liveTranscript?.trim()) {
        const cb = jobCallbacksRef.current[localJobId];
        if (cb) cb(liveTranscript);
        toast.info(`Using live transcript for ${patientName} (Whisper unavailable)`);
      } else {
        toast.error(`Transcription failed for ${patientName}: ${err.message}`);
      }
      setTimeout(() => removeJob(localJobId), 10000);
    }
  }, [api, updateJob, removeJob]);

  const hasActiveJobs = jobs.some(j => j.status === "uploading" || j.status === "transcribing");

  return (
    <BackgroundTranscriptionCtx.Provider value={{ submitTranscription, jobs, hasActiveJobs, removeJob }}>
      {children}
      {/* Floating indicator */}
      {jobs.length > 0 && <TranscriptionFloater jobs={jobs} onDismiss={removeJob} />}
    </BackgroundTranscriptionCtx.Provider>
  );
}

function TranscriptionFloater({ jobs, onDismiss }) {
  const [minimized, setMinimized] = useState(false);

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800 text-white shadow-lg hover:bg-slate-700 transition-colors"
        data-testid="bg-transcription-minimized"
      >
        <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
        <span className="text-xs font-medium">{jobs.filter(j => j.status !== "done" && j.status !== "error").length} transcribing</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden" data-testid="bg-transcription-floater">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800 text-white">
        <div className="flex items-center gap-2">
          <Mic className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-semibold">Background Transcriptions</span>
        </div>
        <button onClick={() => setMinimized(true)} className="text-slate-400 hover:text-white text-xs">
          minimize
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
        {jobs.map(job => (
          <div key={job.id} className="flex items-center gap-2.5 px-3 py-2.5" data-testid={`bg-job-${job.id}`}>
            {job.status === "done" ? (
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : job.status === "error" ? (
              <X className="w-4 h-4 text-red-500 flex-shrink-0" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin text-forest flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-700 truncate">{job.patientName}</p>
              <p className="text-[10px] text-slate-400 truncate">{job.progress}</p>
            </div>
            {(job.status === "done" || job.status === "error") && (
              <button onClick={() => onDismiss(job.id)} className="text-slate-300 hover:text-slate-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
